import { Reducer, Dispatch } from "react";
import { fabric } from "fabric";
import {
  ModelData,
  RegionData,
  UserAction,
  CurrentNavigationKey,
  FabricCanvasObject,
  PriceGroup,
  UserAsset,
} from "./types";
import { CustomCanvasObjectProperties } from "./fabric/constants";

// Actions
const actionRoot = "customization";
const makeAction = (action: string) => `${actionRoot}/${action}`;
const productChanged = makeAction("product_changed");
const regionChanged = makeAction("region_changed");
const navigationKeyChanged = makeAction("navigation_key_changed");
const optionSelected = makeAction("option_selected");
const customizationsUpdated = makeAction("customizations_updated");
const userAssetsAdded = makeAction("user_assets_added");
const cartLoadingChanged = makeAction("cart_loading_changed");
export const actions = {
  productChanged,
  regionChanged,
  navigationKeyChanged,
  optionSelected,
  customizationsUpdated,
  userAssetsAdded,
  cartLoadingChanged,
  fabric: {
    initialized: makeAction("fabric_initialized"),
    fabricRegionChanged: makeAction("fabric_region_changed"),
    updated: makeAction("fabric_updated"),
  },
};

// Reducer
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Action = { type: string; payload: any };
export type State = {
  itemStartingPrice: number;
  loadingCart: boolean;
  userAssets: Lookup<UserAsset>;
  itemNumberId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: Lookup<any>;
  selectedRegion: number;
  currentNavigationKey: CurrentNavigationKey | null;
  navigationViewModelData: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customizableOptions?: any | null;
    priceGroups?: Lookup<PriceGroup> | null;
  };
  regionData: Lookup<RegionData>;
  modelData: ModelData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  productData: any;
  userActions: Lookup<UserAction> | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customerCustomizationData: any[];
  // Fabric
  fabric: {
    canvas: fabric.Canvas;
    regions: Lookup<FabricCanvasObject>;
    activeRegion?: RegionData;
  };
};

const initialState: State = {
  itemStartingPrice: 0,
  loadingCart: false,
  userAssets: {},
  itemNumberId: null,
  items: [],
  selectedRegion: 0,
  currentNavigationKey: null,
  navigationViewModelData: {},
  regionData: {},
  productData: null,
  modelData: {
    model: "",
    material: "",
    textures: {},
    path: "",
  },
  userActions: {},
  customerCustomizationData: [],
  // Fabric
  fabric: {
    canvas: new fabric.Canvas(null),
    regions: {},
    activeRegion: undefined,
  },
};

const reducer: Reducer<State, Action> = (state, { type, payload }) =>
  ({
    [cartLoadingChanged]: { ...state, loadingCart: payload },
    [productChanged]: { ...state, ...payload },
    [regionChanged]: { ...state, selectedRegion: Number(payload) },
    [navigationKeyChanged]: { ...state, currentNavigationKey: payload },
    [optionSelected]: { ...payload },
    [customizationsUpdated]: {
      ...state,
      userActions: { ...payload.userActions },
      customerCustomizationData: [...(payload.customizationData || [])],
    },
    [userAssetsAdded]: {
      ...state,
      userAssets: { ...state.userAssets, ...payload },
    },
    // Fabric
    [actions.fabric.initialized]: {
      ...state,
      fabric: { ...state.fabric, canvas: payload },
    },
    [actions.fabric.fabricRegionChanged]: {
      ...state,
      fabric: { ...state.fabric, activeRegion: payload.region },
    },
    [actions.fabric.updated]: {
      ...state,
      fabric: {
        ...state.fabric,
        canvas: payload.canvas,
        regions: {
          ...state.fabric.regions,
          [payload.regionId]: payload.canvas?.toObject(
            CustomCanvasObjectProperties
          ),
        },
      },
    },
  }[type]);

export { reducer, initialState };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Thunk<T = any> = (
  state: State,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<Action>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => (data?: any) => T | void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const bind = (
  state: State,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-empty-function
  dispatch: Dispatch<any>,
  thunks: Lookup<Thunk>
) =>
  Object.entries(thunks).reduce(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (prev, [name, func]) => ({ ...prev, [name]: func(state, dispatch) }),
    {}
  );
